import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Modal, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';

import {X} from 'components/Icons';
import {hideQuotaPaywall, requestTutorRestart, setBuyMore} from 'store/subscriptionUi';
import {
  changePlan,
  checkoutUrl,
  confirmedQuote,
  getUsage,
  goTo,
  previewChange,
  quoteOffer,
  returnHere,
} from 'utils/billing/billingApi';
import {changeResultLine, changeSummary, isStaleQuote, limitReason} from 'utils/billing/billingModel';
import {loadPlanInfo} from 'utils/billing/planInfo';
import {TEXT_MODE} from 'utils/communication/tutorSessionMeter';
import BuyMoreUsagePanel from './BuyMoreUsagePanel';
import WorldPlans from './WorldPlans';
import {showWorldNotice} from './worldNotice';

import { TYPE, FONTS } from 'style/typography';

const failed = (title, error) => {
  console.error(title, {status: error?.response?.status || null, code: error?.response?.data?.code || null});
  showWorldNotice({title, description: 'Please try again, or contact support if it keeps happening.', tone: 'danger'});
};

function Sheet({visible, title, onClose, children}) {
  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.sheet}>
          <TouchableOpacity accessibilityLabel="Close" style={styles.sheetClose} onPress={onClose}>
            <X size={20} color="#A1A1AA" />
          </TouchableOpacity>
          <Text style={styles.sheetTitle}>{title}</Text>
          {children}
        </View>
      </View>
    </Modal>
  );
}

/**
 * Upgrade in the world (owner 2026-09-29; Sol build-plan D7): every limit, the Upgrade pill and the
 * low-minutes pill open the same full-screen plans here, over the world. A learner without a paid
 * plan goes straight to Stripe Checkout, which returns to this unit; a subscriber confirms the
 * prorated change in one step and stays in the world. "Buy more usage" goes straight to checkout.
 */
export default function WebQuotaPaywall() {
  const dispatch = useDispatch();
  const visible = useSelector(state => Boolean(state.subscriptionUi?.visible));
  const exhausted = useSelector(state => state.subscriptionUi?.exhausted || null);
  const buyMore = useSelector(state => Boolean(state.subscriptionUi?.buyMore));
  const info = useSelector(state => state.subscriptionUi?.plan || null);
  const [usage, setUsage] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  // State updates are asynchronous; a ref closes the double-tap window.
  const inFlight = useRef(false);
  const once = async work => {
    if (inFlight.current) return;
    inFlight.current = true;
    try {
      await work();
    } finally {
      inFlight.current = false;
    }
  };

  // Fresh plan, catalog and balances each time the plans open.
  useEffect(() => {
    if (!visible) return;
    setUsage(null);
    dispatch(loadPlanInfo());
    getUsage().then(setUsage).catch(() => setUsage(null));
  }, [visible, dispatch]);

  const close = useCallback(() => dispatch(hideQuotaPaywall()), [dispatch]);
  const reason = limitReason(exhausted, usage);

  const dismiss = () => {
    close();
    if (reason?.useInstead) dispatch(requestTutorRestart(reason.useInstead === 'voice' ? 'VOICE' : TEXT_MODE));
  };

  const startCheckout = offerId => once(async () => {
    setBusy(true);
    try {
      const catalog = info?.catalog || (await dispatch(loadPlanInfo()))?.catalog;
      if (!catalog) throw new Error('Plan catalog is not ready');
      const quote = await quoteOffer(offerId, catalog);
      if (quote?.provider !== 'stripe') throw new Error('This offer is not available for web checkout');
      const url = await checkoutUrl(offerId, quote.offerSnapshotId, returnHere());
      if (!url) throw new Error('No checkout address');
      goTo(url);
    } catch (error) {
      failed('Could not open checkout', error);
      setBusy(false);
    }
  });

  const requestChange = offerId => once(async () => {
    setBusy(true);
    try {
      const preview = await previewChange(offerId);
      if (preview?.kind === 'noop') {
        showWorldNotice({title: 'No change', description: 'You are already on this plan.'});
        return;
      }
      setPending({offerId, preview});
    } catch (error) {
      failed('Could not change plan', error);
    } finally {
      setBusy(false);
    }
  });

  const confirm = () => once(async () => {
    if (!pending) return;
    setBusy(true);
    let requoted = false;
    try {
      const result = await changePlan(pending.offerId, confirmedQuote(pending.preview));
      showWorldNotice({
        title: result?.kind === 'upgrade_now' ? 'Plan upgraded' : 'Plan change scheduled',
        description: changeResultLine(result),
        tone: 'good',
      });
      close();
      dispatch(loadPlanInfo());
    } catch (error) {
      if (isStaleQuote(error?.response?.data?.code)) {
        try {
          setPending({offerId: pending.offerId, preview: await previewChange(pending.offerId), requoted: true});
          requoted = true;
        } catch (quoteError) {
          failed('Could not change plan', quoteError);
        }
      } else {
        failed('Could not change plan', error);
      }
    } finally {
      setBusy(false);
      if (!requoted) setPending(null);
    }
  });

  const plan = pending ? (info?.plans || []).find(candidate => candidate.planKey === pending.preview.planKey) : null;
  const chosen = pending ? plan?.offers?.find(offer => offer.id === pending.offerId) : null;
  const summary = pending ? changeSummary(pending.preview, plan?.name || pending.preview.planKey, chosen?.billingInterval || 'monthly') : null;

  return (
    <>
      <WorldPlans
        visible={visible}
        reason={reason}
        plans={info?.plans}
        subscription={info?.subscription}
        isFree={info ? info.isFree : true}
        managedBy={info?.managedBy}
        busy={busy}
        loading={!info?.catalog}
        onClose={close}
        onDismiss={dismiss}
        onCheckout={startCheckout}
        onChange={requestChange}
        onBuyMore={() => dispatch(setBuyMore(true))}
      />
      <Sheet visible={buyMore} title="Buy more usage" onClose={() => dispatch(setBuyMore(false))}>
        {buyMore ? <BuyMoreUsagePanel /> : null}
      </Sheet>
      <Sheet visible={Boolean(summary)} title={summary?.title || ''} onClose={() => !busy && setPending(null)}>
        {summary ? (
          <View>
            {pending.requoted ? (
              <Text style={styles.requoted}>The price changed since you opened this. Nothing was charged; please review the new price.</Text>
            ) : null}
            {summary.rows.map(row => (
              <View key={row.label} style={styles.row}>
                <Text style={styles.rowLabel}>{row.label}</Text>
                <Text style={styles.rowValue}>{row.value}</Text>
              </View>
            ))}
            {summary.notes.map(note => <Text key={note} style={styles.note}>{note}</Text>)}
            <View style={styles.actions}>
              <TouchableOpacity style={styles.secondary} disabled={busy} onPress={() => setPending(null)}>
                <Text style={styles.secondaryText}>Not now</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[styles.primary, busy && styles.disabled]} disabled={busy} onPress={confirm}>
                <Text style={styles.primaryText}>{busy ? 'Working...' : summary.confirmLabel}</Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : null}
      </Sheet>
    </>
  );
}

const styles = StyleSheet.create({
  backdrop: {flex: 1, alignItems: 'center', justifyContent: 'center', padding: 16, backgroundColor: 'rgba(0,0,0,0.6)'},
  sheet: {width: '100%', maxWidth: 448, padding: 24, borderRadius: 16, borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)', backgroundColor: '#0D1117'},
  sheetClose: {position: 'absolute', top: 16, right: 16, zIndex: 2, padding: 4},
  sheetTitle: {
    marginBottom: 20,
    paddingRight: 24,
    color: '#F6FBFF',
    fontSize: TYPE.lg,
    fontFamily: FONTS.heading,
    fontWeight: '700',
  },
  requoted: {
    marginBottom: 12,
    color: '#FCD34D',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
  },
  row: {flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 5},
  rowLabel: {
    color: '#A1A1AA',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
  },
  rowValue: {
    marginLeft: 16,
    flexShrink: 1,
    textAlign: 'right',
    color: '#F6FBFF',
    fontSize: TYPE.sm,
    fontFamily: FONTS.body,
    fontWeight: '600',
  },
  note: {
    marginTop: 8,
    color: '#A1A1AA',
    fontSize: TYPE.xs,
    fontFamily: FONTS.body,
  },
  actions: {marginTop: 24, flexDirection: 'row', justifyContent: 'flex-end'},
  secondary: {paddingVertical: 11, paddingHorizontal: 18, marginRight: 10, borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.08)'},
  secondaryText: {
    color: '#F6FBFF',
    fontWeight: '700',
    fontFamily: FONTS.body,
  },
  primary: {paddingVertical: 11, paddingHorizontal: 18, borderRadius: 10, backgroundColor: '#C5E836'},
  primaryText: {
    color: '#10151F',
    fontWeight: '800',
    fontFamily: FONTS.body,
  },
  disabled: {opacity: 0.5},
});
